import { Controller } from "@hotwired/stimulus";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCYb6J5iaho-Gj2sYm-wihHjQlh75eldtU",
  authDomain: "kozenet-pro-ab463.firebaseapp.com",
  projectId: "kozenet-pro-ab463",
  storageBucket: "kozenet-pro-ab463.firebasestorage.app",
  messagingSenderId: "948686418724",
  appId: "1:948686418724:web:696ae352d5b74c255879b1"
};

const registeredTokens = new Set();
const registrationRequests = new Map();

export default class extends Controller {
  static targets = ["bell", "dropdown", "permissionButton"];
  static values = {
    userId: String,
    pollingInterval: { type: Number, default: 60000 },
    vapidKey: String
  };

  connect() {
    this.unreadCount = 0;
    this.messaging = null;
    this.pollingTimer = null;
    this.registration = null;

    this.initializeFCM();
    this.startPolling();
    this.listenForSWMessages();
    this.setupDropdownRefresh();
  }

  disconnect() {
    this.stopPolling();
  }

  // ============ FCM Initialization ============

  async initializeFCM() {
    try {
      if (!('Notification' in window)) return;

      const app = initializeApp(FIREBASE_CONFIG);
      this.messaging = getMessaging(app);

      if (Notification.permission === 'default') {
        this.showPermissionButton();
      } else if (Notification.permission === 'granted') {
        await this.requestToken();
      }

      onMessage(this.messaging, (payload) => {
        this.handleForegroundMessage(payload);
      });

    } catch (error) {
      // Silent fail - polling fallback will handle it
    }
  }

  // ============ Token Management ============

  async requestToken() {
    try {
      if (!this.vapidKeyValue) return;

      const currentToken = await getToken(this.messaging, {
        vapidKey: this.vapidKeyValue,
        serviceWorkerRegistration: await this.getSWRegistration()
      });

      if (currentToken) {
        await this.registerToken(currentToken);
      }
    } catch (error) {
      // Token request failed - will retry on next page load
    }
  }

  async getSWRegistration() {
    if (this.registration) return this.registration;
    
    if ('serviceWorker' in navigator) {
      this.registration = await navigator.serviceWorker.ready;
      return this.registration;
    }
    return null;
  }

  async registerToken(token) {
    if (registeredTokens.has(token)) return;
    if (registrationRequests.has(token)) return registrationRequests.get(token);

    const request = this.sendTokenRegistration(token);
    registrationRequests.set(token, request);

    try {
      await request;
    } catch (error) {
      // Silent fail
    } finally {
      registrationRequests.delete(token);
    }
  }

  async sendTokenRegistration(token) {
    const response = await fetch('/devices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({ token, platform: 'web' })
    });

    if (!response.ok) throw new Error('Device registration failed');
    registeredTokens.add(token);
  }

  // ============ Message Handling ============

  handleForegroundMessage(payload) {
    this.unreadCount++;
    this.updateBadge();
    this.showToast(payload);
    this.refreshDropdown();
  }

  showToast(payload) {
    const title = payload.notification?.title || payload.data?.title || 'Kozenet Pro';
    const body = payload.notification?.body || payload.data?.body || '';

    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 z-[9999] max-w-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-2xl p-4 animate-slide-in';
    toast.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-slate-900 dark:text-white">${this.escapeHtml(title)}</p>
          ${body ? `<p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">${this.escapeHtml(body)}</p>` : ''}
        </div>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('animate-slide-out');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  // ============ Polling ============

  startPolling() {
    this.stopPolling();
    this.pollingTimer = setInterval(() => this.fetchUnreadCount(), this.pollingIntervalValue);
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  async fetchUnreadCount() {
    try {
      const res = await fetch('/notifications/unread_count', {
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.getCSRFToken() },
        credentials: 'same-origin'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.unread_count !== this.unreadCount) {
          this.unreadCount = data.unread_count;
          this.updateBadge();
        }
      }
    } catch (e) { /* silent */ }
  }

  // ============ UI Updates ============

  updateBadge() {
    const bell = this.hasBellTarget ? this.bellTarget : this.element.querySelector('[data-fcm-notifications-target="bell"]');
    if (!bell) return;
    const dot = bell.querySelector('.notification-dot');
    if (dot) dot.classList.toggle('hidden', this.unreadCount === 0);
    const title = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = this.unreadCount > 0 ? `(${this.unreadCount}) ${title}` : title;
  }

  async refreshDropdown() {
    try {
      const response = await fetch('/notifications/dropdown', {
        headers: { 
          'Accept': 'text/vnd.turbo-stream.html', 
          'X-CSRF-Token': this.getCSRFToken() 
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        if (window.Turbo) {
          window.Turbo.renderStreamMessage(html);
        }
      }
    } catch (e) { /* silent */ }
  }

  // ============ Dropdown Refresh on Open ============

  setupDropdownRefresh() {
    const bellButton = this.hasBellTarget ? this.bellTarget : this.element.querySelector('[data-fcm-notifications-target="bell"]');
    
    if (bellButton) {
      bellButton.addEventListener('click', () => {
        setTimeout(() => this.refreshDropdown(), 200);
      });
    }
  }

  // ============ Permission UI ============

  showPermissionButton() {
    if (this.hasPermissionButtonTarget) {
      this.permissionButtonTarget.classList.remove('hidden');
    }
  }

  hidePermissionButton() {
    if (this.hasPermissionButtonTarget) {
      this.permissionButtonTarget.classList.add('hidden');
    }
  }

  // ============ User Actions ============

  async requestPermission() {
    if (!('Notification' in window)) return;

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        this.hidePermissionButton();
        await this.requestToken();
      } else if (permission === 'denied') {
        this.hidePermissionButton();
      }
    } catch (error) {
      // Silent fail
    }
  }

  async markAllAsRead() {
    try {
      await fetch('/notifications/mark_all_as_read', {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': this.getCSRFToken(), 'Accept': 'application/json' }
      });
      this.unreadCount = 0;
      this.updateBadge();
      this.refreshDropdown();
    } catch (e) { /* silent */ }
  }

  async markAsRead(event) {
    const id = event.params?.id;
    if (!id) return;
    try {
      await fetch(`/notifications/${id}/mark_as_read`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': this.getCSRFToken(), 'Accept': 'application/json' }
      });
      if (this.unreadCount > 0) { this.unreadCount--; this.updateBadge(); }
    } catch (e) { /* silent */ }
  }

  // ============ Service Worker ============

  listenForSWMessages() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
          window.location.href = event.data.url;
        }
      });
    }
  }

  // ============ Helpers ============

  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]')?.content || '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
